import moment from 'moment';
import DatePickerConstants from './DatePickerConstants';
import MaxRangeModel from './Model/MaxRangeModel';
import * as dateTypes from './DateTypeConstants';

const maxYear = 99;
const maxRange = new MaxRangeModel(365 * maxYear, 52 * maxYear, 12 * maxYear);

const despatchPerformancePeriod = {
  availablePickers: [DatePickerConstants.WEEK],
  defaultPicker: DatePickerConstants.WEEK,
  defaultRanges: () => {
    const endDateMoment = moment().endOf('isoWeek');
    const startDate = endDateMoment.clone()
      .subtract(5, 'weeks').startOf('isoWeek').toDate();
    const endDate = endDateMoment.toDate();
    return {
      startDate,
      endDate,
      weekPicker: {
        startDate, endDate,
      },
    };
  },
  maxRange: new MaxRangeModel(0, 10, 0),
  availableDateTypes: [
    dateTypes.DESPATCH_CLOSED_DATE,
  ],
  defaultDateType: dateTypes.DESPATCH_CLOSED_DATE,
};

const epgLinkByLinkPeriod = {
  availablePickers: [DatePickerConstants.WEEK],
  defaultPicker: DatePickerConstants.WEEK,
  predefinedRange: date => ({
    startDate: date
      .clone()
      .endOf('isoWeek')
      .subtract(5, 'weeks')
      .add(1, 'days')
      .startOf('isoWeek')
      .toDate(),
    endDate: date
      .clone()
      .endOf('isoWeek')
      .toDate(),
  }),
  defaultRanges: () => {
    const { startDate, endDate } = epgLinkByLinkPeriod
      .predefinedRange(moment().endOf('isoWeek'));
    return {
      startDate,
      endDate,
      weekPicker: {
        startDate,
        endDate,
      },
    };
  },
  maxRange,
  availableDateTypes: [
    dateTypes.DESPATCH_CLOSED_DATE,
  ],
  defaultDateType: dateTypes.DESPATCH_CLOSED_DATE,
};

const epgDespatchClosedPeriod = {
  availablePickers: [DatePickerConstants.MONTH],
  defaultPicker: DatePickerConstants.MONTH,
  predefinedRange: date => ({
    startDate: date
      .clone()
      .endOf('month')
      .subtract(13, 'months')
      .add(1, 'months')
      .startOf('month')
      .toDate(),
    endDate: date
      .clone()
      .endOf('month')
      .toDate(),
  }),
  defaultRanges: () => {
    const { startDate, endDate } = epgDespatchClosedPeriod
      .predefinedRange(moment().endOf('month'));
    return {
      startDate,
      endDate,
      monthPicker: {
        startDate,
        endDate,
      },
    };
  },
  maxRange,
  availableDateTypes: [
    dateTypes.DESPATCH_CLOSED_DATE,
  ],
  defaultDateType: dateTypes.DESPATCH_CLOSED_DATE,
};

const despatchParticularsPeriod = {
  availablePickers: [
    DatePickerConstants.DAY,
    DatePickerConstants.WEEK,
    DatePickerConstants.MONTH],
  defaultPicker: DatePickerConstants.DAY,
  defaultRanges: () => {
    const today = moment().toDate();
    return {
      startDate: today,
      endDate: today,
      dayPicker: {
        startDate: today, endDate: today,
      },
      weekPicker: {
        startDate: moment().startOf('isoWeek').toDate(),
        endDate: moment().endOf('isoWeek').toDate(),
      },
      monthPicker: {
        startDate: moment().startOf('month').toDate(),
        endDate: moment().endOf('month').toDate(),
      },
    };
  },
  maxRange,

  availableDateTypes: [
    dateTypes.DESPATCH_CLOSED_DATE,
    dateTypes.DESPATCH_PLANNED_DEPARTURE_DATE,
    dateTypes.DESPATCH_PLANNED_ARRIVAL_DATE,
  ],
  defaultDateType: dateTypes.DESPATCH_CLOSED_DATE,
};

const despatchMonitoringPeriod = despatchParticularsPeriod;

const despatchMeasurementsDailyEvolutionPeriod = {
  availablePickers: [
    DatePickerConstants.DAY,
  ],
  defaultPicker: DatePickerConstants.DAY,
  defaultRanges: () => {
    const defaultPeriodLength = 30;

    return {
      startDate: moment().subtract(defaultPeriodLength, 'days').toDate(),
      endDate: moment().toDate(),
      dayPicker: {
        startDate: moment().subtract(defaultPeriodLength, 'days').toDate(),
        endDate: moment().toDate(),
      },
    };
  },
  maxRange,

  availableDateTypes: [
    dateTypes.DESPATCH_CLOSED_DATE,
  ],
  defaultDateType: dateTypes.DESPATCH_CLOSED_DATE,
};

const despatchMeasurementsHourlyEvolutionPeriod = {
  availablePickers: [
    DatePickerConstants.DAY,
  ],
  defaultPicker: DatePickerConstants.DAY,
  defaultRanges: () => {
    const defaultPeriodLength = 1;

    return {
      startDate: moment().subtract(defaultPeriodLength, 'days').toDate(),
      endDate: moment().toDate(),
      dayPicker: {
        startDate: moment().subtract(defaultPeriodLength, 'days').toDate(),
        endDate: moment().toDate(),
      },
    };
  },
  maxRange: new MaxRangeModel(1, 0, 0),

  availableDateTypes: [
    dateTypes.DESPATCH_CLOSED_DATE,
  ],
  defaultDateType: dateTypes.DESPATCH_CLOSED_DATE,
};

const complianceMismatchPeriod = {
  availablePickers: [
    DatePickerConstants.DAY,
    DatePickerConstants.WEEK,
    DatePickerConstants.MONTH],
  defaultPicker: DatePickerConstants.DAY,
  defaultRanges: () => {
    const today = moment().toDate();
    return {
      startDate: today,
      endDate: today,
      dayPicker: {
        startDate: today,
        endDate: today,
      },
      weekPicker: {
        startDate: moment().startOf('isoWeek').toDate(),
        endDate: moment().endOf('isoWeek').toDate(),
      },
      monthPicker: {
        startDate: moment().startOf('month').toDate(),
        endDate: moment().endOf('month').toDate(),
      },
    };
  },
  maxRange,

  availableDateTypes: [
    dateTypes.DESPATCH_CLOSED_DATE,
  ],
  defaultDateType: dateTypes.DESPATCH_CLOSED_DATE,
};

const consignmentCompletionDate = {
  availablePickers: [
    DatePickerConstants.DAY,
    DatePickerConstants.WEEK,
    DatePickerConstants.MONTH],
  defaultPicker: DatePickerConstants.DAY,
  defaultRanges: () => {
    const today = moment().toDate();
    return {
      startDate: today,
      endDate: today,
      dayPicker: {
        startDate: today,
        endDate: today,
      },
      weekPicker: {
        startDate: moment().startOf('isoWeek').toDate(),
        endDate: moment().endOf('isoWeek').toDate(),
      },
      monthPicker: {
        startDate: moment().startOf('month').toDate(),
        endDate: moment().endOf('month').toDate(),
      },
    };
  },
  maxRange,

  availableDateTypes: [
    dateTypes.CONSIGNMENT_COMPLETION_DATE,
  ],
  defaultDateType: dateTypes.CONSIGNMENT_COMPLETION_DATE,
};

const receptacleMonitoring = {
  availablePickers: [
    DatePickerConstants.DAY,
    DatePickerConstants.WEEK,
    DatePickerConstants.MONTH],
  defaultPicker: DatePickerConstants.DAY,
  defaultRanges: () => {
    const today = moment().toDate();
    return {
      startDate: today,
      endDate: today,
      dayPicker: {
        startDate: today, endDate: today,
      },
      weekPicker: {
        startDate: moment().startOf('isoWeek').toDate(),
        endDate: moment().endOf('isoWeek').toDate(),
      },
      monthPicker: {
        startDate: moment().startOf('month').toDate(),
        endDate: moment().endOf('month').toDate(),
      },
    };
  },
  maxRange,

  availableDateTypes: [
    dateTypes.DESPATCH_CLOSED_DATE,
    dateTypes.DESPATCH_PLANNED_DEPARTURE_DATE,
    dateTypes.DESPATCH_PLANNED_ARRIVAL_DATE,
  ],
  defaultDateType: dateTypes.DESPATCH_CLOSED_DATE,
};

const interchangePreparationWithAllPickers = {
  availablePickers: [
    DatePickerConstants.DAY,
    DatePickerConstants.WEEK,
    DatePickerConstants.MONTH],
  defaultPicker: DatePickerConstants.DAY,
  defaultRanges: () => {
    const today = moment().toDate();
    return {
      startDate: today,
      endDate: today,
      dayPicker: {
        startDate: today,
        endDate: today,
      },
      weekPicker: {
        startDate: moment().startOf('isoWeek').toDate(),
        endDate: moment().endOf('isoWeek').toDate(),
      },
      monthPicker: {
        startDate: moment().startOf('month').toDate(),
        endDate: moment().endOf('month').toDate(),
      },
    };
  },
  maxRange,

  availableDateTypes: [
    dateTypes.INTERCHANGE_PREPARATION_DATE,
  ],
  defaultDateType: dateTypes.INTERCHANGE_PREPARATION_DATE,
};

const onNetworkDate = {
  availablePickers: [
    DatePickerConstants.DAY,
    DatePickerConstants.WEEK,
    DatePickerConstants.MONTH],
  defaultPicker: DatePickerConstants.DAY,
  defaultRanges: () => {
    const today = moment().toDate();
    return {
      startDate: today,
      endDate: today,
      dayPicker: {
        startDate: today,
        endDate: today,
      },
      weekPicker: {
        startDate: moment().startOf('isoWeek').toDate(),
        endDate: moment().endOf('isoWeek').toDate(),
      },
      monthPicker: {
        startDate: moment().startOf('month').toDate(),
        endDate: moment().endOf('month').toDate(),
      },
    };
  },
  maxRange,

  availableDateTypes: [
    dateTypes.ON_NETWORK_DATE,
  ],
  defaultDateType: dateTypes.ON_NETWORK_DATE,
};

const postalScanDate = {
  availablePickers: [
    DatePickerConstants.DAY,
    DatePickerConstants.WEEK,
    DatePickerConstants.MONTH],
  defaultPicker: DatePickerConstants.DAY,
  defaultRanges: () => {
    const today = moment().toDate();
    return {
      startDate: today,
      endDate: today,
      dayPicker: {
        startDate: today,
        endDate: today,
      },
      weekPicker: {
        startDate: moment().startOf('isoWeek').toDate(),
        endDate: moment().endOf('isoWeek').toDate(),
      },
      monthPicker: {
        startDate: moment().startOf('month').toDate(),
        endDate: moment().endOf('month').toDate(),
      },
    };
  },
  maxRange,

  availableDateTypes: [
    dateTypes.POSTAL_SCAN_DATE,
  ],
  defaultDateType: dateTypes.POSTAL_SCAN_DATE,
};

const mrdPodOverview = {
  availablePickers: [
    DatePickerConstants.DAY,
    DatePickerConstants.WEEK,
    DatePickerConstants.MONTH],
  defaultPicker: DatePickerConstants.DAY,
  defaultRanges: () => {
    const today = moment().toDate();
    return {
      startDate: today,
      endDate: today,
      dayPicker: {
        startDate: today,
        endDate: today,
      },
      weekPicker: {
        startDate: moment().startOf('isoWeek').toDate(),
        endDate: moment().endOf('isoWeek').toDate(),
      },
      monthPicker: {
        startDate: moment().startOf('month').toDate(),
        endDate: moment().endOf('month').toDate(),
      },
    };
  },
  maxRange,

  availableDateTypes: [

  ],
};

const handoverRegistrationTime = {
  availablePickers: [
    DatePickerConstants.DAY,
    DatePickerConstants.WEEK,
    DatePickerConstants.MONTH],
  defaultPicker: DatePickerConstants.DAY,
  defaultRanges: () => {
    const today = moment().toDate();
    return {
      startDate: today,
      endDate: today,
      dayPicker: {
        startDate: today,
        endDate: today,
      },
      weekPicker: {
        startDate: moment().startOf('isoWeek').toDate(),
        endDate: moment().endOf('isoWeek').toDate(),
      },
      monthPicker: {
        startDate: moment().startOf('month').toDate(),
        endDate: moment().endOf('month').toDate(),
      },
    };
  },
  maxRange,

  availableDateTypes: [
    dateTypes.HANDOVER_REGISTRATION_TIME,
  ],
  defaultDateType: dateTypes.HANDOVER_REGISTRATION_TIME,
};

const carditConsignmentCompletionDate = {
  availablePickers: [
    DatePickerConstants.DAY,
    DatePickerConstants.WEEK,
    DatePickerConstants.MONTH],
  defaultPicker: DatePickerConstants.DAY,
  defaultRanges: () => {
    const today = moment().toDate();
    return {
      startDate: today,
      endDate: today,
      dayPicker: {
        startDate: today,
        endDate: today,
      },
      weekPicker: {
        startDate: moment().startOf('isoWeek').toDate(),
        endDate: moment().endOf('isoWeek').toDate(),
      },
      monthPicker: {
        startDate: moment().startOf('month').toDate(),
        endDate: moment().endOf('month').toDate(),
      },
    };
  },
  maxRange,

  availableDateTypes: [
    dateTypes.CARDIT_CONSIGNMENT_COMPLETION_DATE,
  ],
  defaultDateType: dateTypes.CARDIT_CONSIGNMENT_COMPLETION_DATE,
};

const carditFirstLegPlannedDepartureDate = {
  availablePickers: [
    DatePickerConstants.DAY,
    DatePickerConstants.WEEK,
    DatePickerConstants.MONTH],
  defaultPicker: DatePickerConstants.DAY,
  defaultRanges: () => {
    const today = moment().toDate();
    return {
      startDate: today,
      endDate: today,
      dayPicker: {
        startDate: today,
        endDate: today,
      },
      weekPicker: {
        startDate: moment().startOf('isoWeek').toDate(),
        endDate: moment().endOf('isoWeek').toDate(),
      },
      monthPicker: {
        startDate: moment().startOf('month').toDate(),
        endDate: moment().endOf('month').toDate(),
      },
    };
  },
  maxRange,

  availableDateTypes: [
    dateTypes.CARDIT_FIRST_LEG_PLANNED_DEPARTURE_DATE,
  ],
  defaultDateType: dateTypes.CARDIT_FIRST_LEG_PLANNED_DEPARTURE_DATE,
};

const handoverRegistrationDate = {
  availablePickers: [
    DatePickerConstants.DAY,
    DatePickerConstants.WEEK,
    DatePickerConstants.MONTH],
  defaultPicker: DatePickerConstants.DAY,
  defaultRanges: () => {
    const today = moment().toDate();
    return {
      startDate: today,
      endDate: today,
      dayPicker: {
        startDate: today,
        endDate: today,
      },
      weekPicker: {
        startDate: moment().startOf('isoWeek').toDate(),
        endDate: moment().endOf('isoWeek').toDate(),
      },
      monthPicker: {
        startDate: moment().startOf('month').toDate(),
        endDate: moment().endOf('month').toDate(),
      },
    };
  },
  maxRange,

  availableDateTypes: [
    dateTypes.HANDOVER_REGISTRATION_DATE,
  ],
  defaultDateType: dateTypes.HANDOVER_REGISTRATION_DATE,
};

const mrdHandoverRegistrationDate = {
  availablePickers: [
    DatePickerConstants.DAY,
    DatePickerConstants.WEEK,
    DatePickerConstants.MONTH],
  defaultPicker: DatePickerConstants.DAY,
  defaultRanges: () => {
    const today = moment().toDate();
    return {
      startDate: today,
      endDate: today,
      dayPicker: {
        startDate: today,
        endDate: today,
      },
      weekPicker: {
        startDate: moment().startOf('isoWeek').toDate(),
        endDate: moment().endOf('isoWeek').toDate(),
      },
      monthPicker: {
        startDate: moment().startOf('month').toDate(),
        endDate: moment().endOf('month').toDate(),
      },
    };
  },
  maxRange,

  availableDateTypes: [
    dateTypes.MRD_HANDOVER_REGISTRATION_DATE,
  ],
  defaultDateType: dateTypes.MRD_HANDOVER_REGISTRATION_DATE,
};

const messagePreparationDate = {
  availablePickers: [
    DatePickerConstants.DAY,
    DatePickerConstants.WEEK,
    DatePickerConstants.MONTH],
  defaultPicker: DatePickerConstants.DAY,
  defaultRanges: () => {
    const today = moment().toDate();
    return {
      startDate: today,
      endDate: today,
      dayPicker: {
        startDate: today,
        endDate: today,
      },
      weekPicker: {
        startDate: moment().startOf('isoWeek').toDate(),
        endDate: moment().endOf('isoWeek').toDate(),
      },
      monthPicker: {
        startDate: moment().startOf('month').toDate(),
        endDate: moment().endOf('month').toDate(),
      },
    };
  },
  maxRange,

  availableDateTypes: [
    dateTypes.MESSAGE_PREPARATION_DATE,
  ],
  defaultDateType: dateTypes.MESSAGE_PREPARATION_DATE,
};

const receptacleTransportMonitoringOverview = {
  availablePickers: [
    DatePickerConstants.DAY,
    DatePickerConstants.WEEK,
    DatePickerConstants.MONTH],
  defaultPicker: DatePickerConstants.DAY,
  defaultRanges: () => {
    const today = moment().toDate();
    return {
      startDate: today,
      endDate: today,
      dayPicker: {
        startDate: today, endDate: today,
      },
      weekPicker: {
        startDate: moment().startOf('isoWeek').toDate(),
        endDate: moment().endOf('isoWeek').toDate(),
      },
      monthPicker: {
        startDate: moment().startOf('month').toDate(),
        endDate: moment().endOf('month').toDate(),
      },
    };
  },
  maxRange,

  availableDateTypes: [
    dateTypes.DESPATCH_CLOSED_DATE,
    dateTypes.CARDIT_PLANNED_DEPARTURE_DATE,
    dateTypes.CARDIT_PLANNED_ARRIVAL_DATE,
  ],
  defaultDateType: dateTypes.DESPATCH_CLOSED_DATE,
};

const period = {
  availablePickers: [
    DatePickerConstants.DAY,
    DatePickerConstants.WEEK,
    DatePickerConstants.MONTH],
  defaultPicker: DatePickerConstants.DAY,
  defaultRanges: () => {
    const today = moment().toDate();
    return {
      startDate: today,
      endDate: today,
      dayPicker: {
        startDate: today,
        endDate: today,
      },
      weekPicker: {
        startDate: moment().startOf('isoWeek').toDate(),
        endDate: moment().endOf('isoWeek').toDate(),
      },
      monthPicker: {
        startDate: moment().startOf('month').toDate(),
        endDate: moment().endOf('month').toDate(),
      },
    };
  },
  maxRange,

  availableDateTypes: [
    dateTypes.PERIOD,
  ],
  defaultDateType: dateTypes.PERIOD,
};

const baseDateRange = {
  availablePickers: [
    DatePickerConstants.DAY,
    DatePickerConstants.WEEK,
    DatePickerConstants.MONTH],
  defaultPicker: DatePickerConstants.DAY,
  defaultRanges: () => {
    const today = moment().toDate();
    return {
      startDate: today,
      endDate: today,
      dayPicker: {
        startDate: today,
        endDate: today,
      },
      weekPicker: {
        startDate: moment().startOf('isoWeek').toDate(),
        endDate: moment().endOf('isoWeek').toDate(),
      },
      monthPicker: {
        startDate: moment().startOf('month').toDate(),
        endDate: moment().endOf('month').toDate(),
      },
    };
  },
  maxRange,

  availableDateTypes: [
    dateTypes.BASE_DATE_RANGE,
  ],
  defaultDateType: dateTypes.BASE_DATE_RANGE,
};

const eventDate = {
  availablePickers: [
    DatePickerConstants.DAY,
    DatePickerConstants.WEEK,
    DatePickerConstants.MONTH],
  defaultPicker: DatePickerConstants.DAY,
  defaultRanges: () => {
    const today = moment().toDate();
    return {
      startDate: today,
      endDate: today,
      dayPicker: {
        startDate: today,
        endDate: today,
      },
      weekPicker: {
        startDate: moment().startOf('isoWeek').toDate(),
        endDate: moment().endOf('isoWeek').toDate(),
      },
      monthPicker: {
        startDate: moment().startOf('month').toDate(),
        endDate: moment().endOf('month').toDate(),
      },
    };
  },
  maxRange,

  availableDateTypes: [
    dateTypes.EVENT_DATE,
  ],
  defaultDateType: dateTypes.EVENT_DATE,
};

const reportGenerationDate = {
  availablePickers: [
    DatePickerConstants.DAY,
    DatePickerConstants.WEEK,
    DatePickerConstants.MONTH],
  defaultPicker: DatePickerConstants.DAY,
  defaultRanges: () => {
    const today = moment().toDate();
    return {
      startDate: today,
      endDate: today,
      dayPicker: {
        startDate: today,
        endDate: today,
      },
      weekPicker: {
        startDate: moment().startOf('isoWeek').toDate(),
        endDate: moment().endOf('isoWeek').toDate(),
      },
      monthPicker: {
        startDate: moment().startOf('month').toDate(),
        endDate: moment().endOf('month').toDate(),
      },
    };
  },
  maxRange,

  availableDateTypes: [
    dateTypes.REPORT_GENERATION_DATE,
  ],
  defaultDateType: dateTypes.REPORT_GENERATION_DATE,
};

const entryDate = {
  availablePickers: [
    DatePickerConstants.DAY,
    DatePickerConstants.WEEK,
    DatePickerConstants.MONTH],
  defaultPicker: DatePickerConstants.DAY,
  defaultRanges: () => {
    const today = moment().toDate();
    return {
      startDate: today,
      endDate: today,
      dayPicker: {
        startDate: today,
        endDate: today,
      },
      weekPicker: {
        startDate: moment().startOf('isoWeek').toDate(),
        endDate: moment().endOf('isoWeek').toDate(),
      },
      monthPicker: {
        startDate: moment().startOf('month').toDate(),
        endDate: moment().endOf('month').toDate(),
      },
    };
  },
  maxRange,

  availableDateTypes: [
    dateTypes.ENTRY_DATE,
    dateTypes.LEG3_START_DATE,
  ],
  defaultDateType: dateTypes.ENTRY_DATE,
};

const despatchPlannedArrivalDate = {
  availablePickers: [
    DatePickerConstants.DAY,
    DatePickerConstants.WEEK,
    DatePickerConstants.MONTH],
  defaultPicker: DatePickerConstants.DAY,
  defaultRanges: () => {
    const today = moment().toDate();
    return {
      startDate: today,
      endDate: today,
      dayPicker: {
        startDate: today,
        endDate: today,
      },
      weekPicker: {
        startDate: moment().startOf('isoWeek').toDate(),
        endDate: moment().endOf('isoWeek').toDate(),
      },
      monthPicker: {
        startDate: moment().startOf('month').toDate(),
        endDate: moment().endOf('month').toDate(),
      },
    };
  },
  maxRange,

  availableDateTypes: [
    dateTypes.DESPATCH_PLANNED_ARRIVAL_DATE,
  ],
  defaultDateType: dateTypes.DESPATCH_PLANNED_ARRIVAL_DATE,
};

export default {
  despatchPerformancePeriod,
  despatchParticularsPeriod,
  despatchMonitoringPeriod,
  despatchMeasurementsDailyEvolutionPeriod,
  despatchMeasurementsHourlyEvolutionPeriod,
  complianceMismatchPeriod,
  consignmentCompletionDate,
  receptacleMonitoring,
  interchangePreparationWithAllPickers,
  onNetworkDate,
  postalScanDate,
  mrdPodOverview,
  handoverRegistrationTime,
  carditConsignmentCompletionDate,
  carditFirstLegPlannedDepartureDate,
  handoverRegistrationDate,
  messagePreparationDate,
  receptacleTransportMonitoringOverview,
  mrdHandoverRegistrationDate,
  epgDespatchClosedPeriod,
  epgLinkByLinkPeriod,
  period,
  baseDateRange,
  eventDate,
  reportGenerationDate,
  entryDate,
  despatchPlannedArrivalDate,
};
